import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Compass, Feather, Trophy, Camera, ChevronDown } from "lucide-react";
import TimeOfDayBadge from "@/components/TimeOfDayBadge";
import SoulCompass from "@/components/SoulCompass";
import N8Emblem from "@/components/N8Emblem";
import LongevityMilestones from "@/components/LongevityMilestones";
import BecomingGallery from "@/components/BecomingGallery";
import SacredAgingProfile from "@/components/SacredAgingProfile";
import ProGate from "@/components/ProGate";
import MonthlyBecomingReflection from "@/components/MonthlyBecomingReflection";
import CompassEvolvingInsights from "@/components/CompassEvolvingInsights";
import Tracking from "@/pages/Tracking";
import TrackingHeatmap from "@/components/TrackingHeatmap";
import { useSubscription } from "@/context/SubscriptionContext";
import PageHero from "@/components/PageHero";
import { HERO } from "@/lib/heroImages";

/**
 * Becoming — the personal journey from self-discovery to intentional growth.
 *
 *   Discover   → Soul Compass ("Who am I?")
 *   Understand → Soul Work + Intentional Self ("What shapes me?")
 *   Choose     → My Journey (goals + progress) + Daily Tracking ("The actions I'm choosing.")
 *   Capture    → Honor gallery ("The moments I'm choosing to preserve.")
 *
 * Composes existing feature components; nothing new is invented. The four
 * rooms are surfaced as pills so the arc reads as one journey rather than
 * four separate features. The section the user was last in is remembered
 * across visits via sessionStorage.
 */
const SECTIONS = [
  { key: "discover",   label: "Discover",         hint: "Soul Compass",      Icon: Compass,  testid: "becoming-section-discover" },
  { key: "understand", label: "Intentional Self", hint: "The Art Of Becoming", Icon: Feather,  testid: "becoming-section-understand" },
  { key: "choose",     label: "Choose",           hint: "My Journey",        Icon: Trophy,   testid: "becoming-section-choose" },
  { key: "capture",    label: "Capture",          hint: "Honor",             Icon: Camera,   testid: "becoming-section-capture" },
];

// Backward-compat: URL/session values used before this restructure map to
// the corresponding new section so old bookmarks keep working.
const LEGACY_SECTION_MAP = {
  witness: "understand",
  gallery: "capture",
};

export default function Becoming() {
  const [params, setParams] = useSearchParams();
  const seedJournalId = params.get("seed");
  const [profile, setProfile] = useState(null);
  const sub = useSubscription();
  const [section, setSection] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("section");
    const resolve = (v) => LEGACY_SECTION_MAP[v] || v;
    if (q) {
      const r = resolve(q);
      if (SECTIONS.some((s) => s.key === r)) return r;
    }
    try {
      const stored = sessionStorage.getItem("becoming-section") || "discover";
      const r = resolve(stored);
      if (SECTIONS.some((s) => s.key === r)) return r;
      return "discover";
    } catch { return "discover"; }
  });

  useEffect(() => {
    try { sessionStorage.setItem("becoming-section", section); } catch { /* noop */ }
    const next = new URLSearchParams(params);
    if (section === "discover") next.delete("section");
    else next.set("section", section);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const loadProfile = async () => {
    try { const { data } = await endpoints.profile(); setProfile(data); }
    catch { /* silent */ }
  };
  useEffect(() => { loadProfile(); }, []);

  const active = SECTIONS.find((s) => s.key === section) || SECTIONS[0];

  return (
    <div className="space-y-10" data-testid="becoming-page">
      <PageHero
        eyebrow={
          <span className="inline-flex items-center gap-3">
            <span>Your Journey</span>
            <span className="opacity-50" aria-hidden>·</span>
            <TimeOfDayBadge testid="becoming-time-of-day" />
          </span>
        }
        title="Becoming"
        supporting="Discover yourself. Understand what shapes you. Choose who you're becoming."
        body="The more honestly you tend to this journey, the more meaningful every ritual, reflection, and recommendation across N8One becomes."
        image={HERO.becoming}
        testid="becoming-hero"
      />

      {/* Section pills — 4 rooms, one arc. Vertical labels + hints so the
          journey reads as a story, not just a tab set.
          A static N8ONE emblem sits above the arc to mark Becoming as a
          major N8ONE experience without decorating the hero banner. */}
      <div className="flex items-center justify-center" data-testid="becoming-emblem-wrap">
        <N8Emblem
          mode="full"
          size={56}
          ariaLabel="N8ONE emblem"
          testid="becoming-emblem"
        />
      </div>
      <nav
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 reveal reveal-1"
        data-testid="becoming-sections"
        role="tablist"
        aria-label="Becoming journey"
      >
        {SECTIONS.map((s, i) => {
          const on = section === s.key;
          const Icon = s.Icon;
          return (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setSection(s.key)}
              data-testid={s.testid}
              className={`group p-5 border rounded-md text-left tab-link transition-all ${
                on
                  ? "bg-[hsl(var(--terracotta))]/12 border-[hsl(var(--terracotta))]/50 shadow-[inset_3px_0_0_hsl(var(--terracotta))]"
                  : "hairline hover:bg-[hsl(var(--sand))]/40 bg-[hsl(var(--paper))]"
              }`}
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                <span className="tabular-nums">0{i + 1}</span>
                <span>{s.hint}</span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Icon size={16} className={on ? "text-[hsl(var(--terracotta))]" : "text-[hsl(var(--clay))]"} />
                <span className="font-display text-xl">{s.label}</span>
              </div>
            </button>
          );
        })}
      </nav>

      <section
        className="reveal reveal-2"
        data-testid={`becoming-panel-${active.key}`}
      >
        {section === "discover" && (
          <div className="space-y-8">
            <SoulCompass />
            <ProGate
              feature="soul_compass_advanced"
              title="Deepen your Compass with Pro."
              blurb="Free gives you your archetype and core values. Pro helps you discover the deeper patterns, themes, and growth opportunities within them — and see how your Compass evolves as you do."
              bullets={[
                "Discover the patterns connecting your responses",
                "Explore deeper themes and areas for growth",
                "Revisit your Compass and see what changes over time",
                "Receive guidance that evolves with you",
              ]}
              testid="pro-gate-soul_compass_advanced"
            >
              <CompassEvolvingInsights />
            </ProGate>
          </div>
        )}
        {section === "understand" && (
          <div className="space-y-10" data-testid="becoming-understand-panel">
            {profile ? (
              <SacredAgingProfile
                profile={profile}
                onBack={() => setSection("understand")}
                onSaved={loadProfile}
              />
            ) : (
              <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="becoming-witness-loading">
                Steeping…
              </div>
            )}
          </div>
        )}
        {section === "choose" && (
          <div className="space-y-8" data-testid="becoming-choose-panel">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
              <Trophy size={12} /> The Actions I'm Choosing
            </div>

            <section
              className="p-6 border hairline rounded-md bg-[hsl(var(--paper))]"
              data-testid="becoming-choose-journey-section"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                Your current goals
              </div>
              <div className="font-display text-xl italic mt-0.5">The areas you're choosing to nurture</div>
              <div className="mt-4">
                <LongevityMilestones
                  isPro={sub.isPro}
                  onEnablePro={() => sub.openPricing({
                    feature: "becoming_insights",
                    title: "Go deeper into your Becoming.",
                    blurb: "You have your goals and your daily practice. Pro reveals the patterns connecting your choices to the person you're becoming — evolving insights, ritual consistency reflections, and monthly Becoming reflections that grow with you.",
                  })}
                />
              </div>
            </section>

            <div data-testid="becoming-choose-tracking">
              <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                Daily Tracking
              </div>
              <p className="mt-1 max-w-2xl text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                The small, honest actions you're choosing today. Every check-in is quiet proof that your goals are
                becoming a life.
              </p>
              <div className="mt-6">
                <TrackingHeatmap days={91} />
              </div>
              <div className="mt-6">
                <Tracking />
              </div>
            </div>

            <ProGate
              feature="monthly_becoming_reflection"
              title="A reflection that evolves with you."
              blurb="On the last day of every month, N8 Guide gathers what you explored, practiced, and learned — and writes back a personal reflection on who you're becoming, one that grows richer as your journey does."
              bullets={[
                "What you explored and practiced",
                "Themes that appeared across your journal and Soul Work",
                "Where you showed up more consistently",
                "A gentle focus suggestion for the next month",
              ]}
              testid="pro-gate-monthly_becoming_reflection"
            >
              <MonthlyBecomingReflection />
            </ProGate>
          </div>
        )}
        {section === "capture" && (
          <BecomingGallery onBack={() => setSection("choose")} />
        )}
      </section>
    </div>
  );
}
